'ues client';

import {Box, Colors, Icon, TextInput, Tooltip} from '@dagster-io/ui-components';
import Cookies from 'js-cookie';
import {useSearchParams} from 'next/navigation';
import {useReCaptcha} from 'next-recaptcha-v3';
import {ChangeEvent, FormEvent, ReactNode, useState} from 'react';

import CenteredButton from '@/components/CenteredButton';
import {analytics} from '@/lib/analytics';
import {validateEmail} from '@/utils/validateEmail';

interface Props {
  buttonLabel: ReactNode;
  submitPath: string;
  onDidSendEmail: () => void;
}

export default function EmailForm({buttonLabel, submitPath, onDidSendEmail}: Props) {
  const searchParams = useSearchParams();

  const [email, setEmail] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const {executeRecaptcha} = useReCaptcha();

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setInputError(null);
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validation = validateEmail(email);
    if (validation.error) {
      setInputError(validation.error);
      setIsSubmitting(false);
      return;
    }

    setSubmissionError(null);
    setIsSubmitting(true);
    const token = await executeRecaptcha('signup');

    const nextParams = new URLSearchParams(searchParams || '');
    nextParams.append('recaptcha_token', token);

    const url = new URL(`${window.location.origin}${submitPath}`);
    url.search = nextParams.toString();

    const analyticsUser = await analytics?.user();

    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    const csrfToken = Cookies.get('X-CSRF-TOKEN');
    if (csrfToken) {
      headers.set('X-CSRF-TOKEN', csrfToken);
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        anonymous_id: analyticsUser ? analyticsUser.anonymousId() : null,
        email,
      }),
    });

    setIsSubmitting(false);
    if (response.status === 200) {
      onDidSendEmail();
    } else {
      const {error} = await response.json();
      setSubmissionError(error);
    }
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <Box flex={{direction: 'column', gap: 16, alignItems: 'stretch'}}>
        <TextInput
          placeholder="Enter your email"
          value={email}
          type="email"
          onChange={onChange}
          strokeColor={inputError ? Colors.accentRed() : undefined}
          rightElement={
            inputError ? (
              <Tooltip content={inputError} placement="top">
                <Icon name="warning" color={Colors.accentRed()} />
              </Tooltip>
            ) : undefined
          }
        />
        <CenteredButton
          type="submit"
          intent="primary"
          label={buttonLabel}
          disabled={isSubmitting}
        />
        {submissionError ? (
          <Box
            flex={{direction: 'row', gap: 4, alignItems: 'center'}}
            style={{color: Colors.textRed(), margin: '0 auto'}}
          >
            <Icon name="warning_outline" color={Colors.textRed()} />
            {submissionError}
          </Box>
        ) : null}
      </Box>
    </form>
  );
}
